import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Form,
  Input,
  Button,
  Space,
  Row,
  Col,
  DatePicker,
  Tooltip,
  Divider,
  message,
} from "antd";
import type { TableProps } from "antd";
import request from "../../../utils/request";
import { ChatHistoryItem, TabProps } from "../../../types/dataControl";
import ChatDetailModal from "./modals/ChatDetailModal";

const { RangePicker } = DatePicker;

const ChatHistoryTab: React.FC<TabProps> = ({ API_BASE_URL, token }) => {
  const [chatList, setChatList] = useState<ChatHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [selectedChat, setSelectedChat] = useState<ChatHistoryItem | null>(
    null
  );
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [form] = Form.useForm();

  // 获取聊天记录列表
  const fetchChatHistory = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      // 添加筛选条件
      if (values.open_id) params.open_id = values.open_id;
      if (values.project_key) params.project_key = values.project_key;
      if (values.chat_id) params.chat_id = values.chat_id;
      if (values.en_id) params.en_id = values.en_id;

      // 处理时间范围
      if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
        params.start_time = values.dateRange[0].format("YYYY-MM-DD");
        params.end_time = values.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await request.get<{
        code: number;
        msg: string;
        data: { total: number; list: ChatHistoryItem[] };
      }>(`${API_BASE_URL}/api/v3/admin/user/chatHistory`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setChatList(response.data.data.list);
        setTotal(response.data.data.total);
      } else {
        message.error(response.data.msg || "获取聊天记录失败");
      }
    } catch (error) {
      console.error("获取聊天记录失败:", error);
      message.error("获取聊天记录失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, form, pagination]);

  // 初始加载数据
  useEffect(() => {
    fetchChatHistory();
  }, [fetchChatHistory]);

  // 处理表格变化
  const handleTableChange: TableProps<ChatHistoryItem>["onChange"] = (
    pagination
  ) => {
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  // 处理查询
  const handleSearch = () => {
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchChatHistory();
  };

  // 重置筛选
  const handleReset = () => {
    form.resetFields();
    setPagination({
      current: 1,
      pageSize: 10,
    });
    fetchChatHistory();
  };

  // 查看聊天详情
  const viewDetail = (record: ChatHistoryItem) => {
    setSelectedChat(record);
    setModalVisible(true);
  };

  // 定义表格列
  const columns: TableProps<ChatHistoryItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "用户ID/邮箱",
      key: "user",
      width: 180,
      render: (_, record) => (
        <div>
          <div>UID: {record.open_id}</div>
          <div>{record.email !== "NULL" ? record.email : "-"}</div>
        </div>
      ),
    },
    {
      title: "项目Key",
      dataIndex: "project_key",
      key: "project_key",
      width: 180,
      ellipsis: true,
    },
    {
      title: "环境ID",
      dataIndex: "en_id",
      key: "en_id",
      width: 120,
    },
    {
      title: "会话ID",
      dataIndex: "chat_id",
      key: "chat_id",
      width: 120,
    },
    {
      title: "消息数量",
      key: "message_count",
      width: 100,
      render: (_, record) => record.message?.messages?.length || 0,
    },
    {
      title: "对话内容预览",
      key: "content_preview",
      ellipsis: {
        showTitle: false,
      },
      render: (_, record) => {
        const messages = record.message?.messages || [];
        if (messages.length === 0) return "-";

        const firstUserMessage =
          messages.find((msg) => msg.role === "user")?.content || "";
        return (
          <Tooltip title={firstUserMessage}>
            <span>{firstUserMessage}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (date) => new Date(date).toLocaleString("zh-CN"),
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => viewDetail(record)}
          style={{ padding: 0 }}
        >
          查看详情
        </Button>
      ),
    },
  ];

  return (
    <>
      {/* 聊天记录筛选表单 */}
      <Form form={form} layout="vertical" onFinish={handleSearch}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="用户ID" name="open_id">
              <Input placeholder="请输入用户ID" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="项目Key" name="project_key">
              <Input placeholder="请输入项目Key" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="环境ID" name="en_id">
              <Input placeholder="请输入环境ID" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="会话ID" name="chat_id">
              <Input placeholder="请输入会话ID" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8}>
            <Form.Item label="时间范围" name="dateRange">
              <RangePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Form.Item label=" " colon={false}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={
                    <i className="ri-search-line" style={{ marginRight: 4 }} />
                  }
                >
                  查询
                </Button>
                <Button
                  onClick={handleReset}
                  icon={
                    <i className="ri-refresh-line" style={{ marginRight: 4 }} />
                  }
                >
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider style={{ margin: "12px 0 24px" }} />

      {/* 聊天记录列表表格 */}
      <Table
        columns={columns}
        dataSource={chatList}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1100 }}
      />

      {/* 聊天详情模态框 */}
      <ChatDetailModal
        visible={modalVisible}
        data={selectedChat}
        onClose={() => setModalVisible(false)}
      />
    </>
  );
};

export default ChatHistoryTab;
